// src/services/admin/affiliations.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const affiliationsApi = createApi({
  reducerPath: "affiliations",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api/admin/affiliations`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Affiliations"],
  endpoints: (builder) => ({
    getAffiliations: builder.query({
      query: ({
        page,
        limit,
        search = "",
        sortField,
        sortOrder,
        filterCity,
        filterActive,
      }) => {
        let queryString = `?page=${encodeURIComponent(page ?? 1)}`;
        if (limit !== undefined)
          queryString += `&limit=${encodeURIComponent(limit)}`;
        if (search)
          queryString += `&search=${encodeURIComponent(search)}`;
        if (sortField)
          queryString += `&sortField=${encodeURIComponent(sortField)}`;
        if (sortOrder)
          queryString += `&sortOrder=${encodeURIComponent(sortOrder)}`;
        if (filterCity)
          queryString += `&filterCity=${encodeURIComponent(filterCity)}`;
        if (filterActive !== undefined)
          queryString += `&filterActive=${encodeURIComponent(filterActive)}`;
        return queryString;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Affiliations", id })),
              { type: "Affiliations", id: "PARTIAL-LIST" },
            ]
          : [{ type: "Affiliations", id: "PARTIAL-LIST" }],
    }),
    getBaseAffiliations: builder.query({
      query: ({
        page,
        limit,
        search = "",
        sortField,
        sortOrder,
        filterCity,
        filterActive,
      }) => {
        let queryString = `/base?page=${encodeURIComponent(page ?? 1)}`;
        if (limit !== undefined)
          queryString += `&limit=${encodeURIComponent(limit)}`;
        if (search)
          queryString += `&search=${encodeURIComponent(search)}`;
        if (sortField)
          queryString += `&sortField=${encodeURIComponent(sortField)}`;
        if (sortOrder)
          queryString += `&sortOrder=${encodeURIComponent(sortOrder)}`;
        if (filterCity)
          queryString += `&filterCity=${encodeURIComponent(filterCity)}`;
        if (filterActive !== undefined)
          queryString += `&filterActive=${encodeURIComponent(filterActive)}`;
        return queryString;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Affiliations", id })),
              { type: "Affiliations", id: "BASE-LIST" },
            ]
          : [{ type: "Affiliations", id: "BASE-LIST" }],
    }),
    getScholarshipAffiliations: builder.query({
      query: ({
        page,
        limit,
        search = "",
        sortField,
        sortOrder,
        filterCity,
        filterActive,
      }) => {
        let queryString = `sa?page=${encodeURIComponent(page ?? 1)}`;
        if (limit !== undefined)
          queryString += `&limit=${encodeURIComponent(limit)}`;
        if (search)
          queryString += `&search=${encodeURIComponent(search)}`;
        if (sortField)
          queryString += `&sortField=${encodeURIComponent(sortField)}`;
        if (sortOrder)
          queryString += `&sortOrder=${encodeURIComponent(sortOrder)}`;
        if (filterCity)
          queryString += `&filterCity=${encodeURIComponent(filterCity)}`;
        if (filterActive !== undefined)
          queryString += `&filterActive=${encodeURIComponent(filterActive)}`;

        return queryString;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: "ScholarshipAffiliations",
                id,
              })),
              { type: "ScholarshipAffiliations", id: "PARTIAL-LIST" },
            ]
          : [{ type: "ScholarshipAffiliations", id: "PARTIAL-LIST" }],
    }),
    getAffiliationById: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "Affiliations", id }],
    }),

    // New endpoint: affiliations with scholarships
    getAffiliationsWithScholarships: builder.query({
      query: () => "/with-scholarships",
      providesTags: (result) => {
        const arr = result && Array.isArray(result.data) ? result.data : [];
        return arr.length > 0
          ? [
              ...arr.map(({ id }) => ({ type: "Affiliations", id })),
              { type: "Affiliations", id: "WITH-SCHOLARSHIPS" },
            ]
          : [{ type: "Affiliations", id: "WITH-SCHOLARSHIPS" }];
      },
    }),
    createAffiliation: builder.mutation({
      query: (newAff) => ({
        url: "/",
        method: "POST",
        body: newAff,
      }),
      invalidatesTags: [
        { type: "Affiliations", id: "PARTIAL-LIST" },
        { type: "Affiliations", id: "BASE-LIST" },
      ],
    }),
    updateAffiliation: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `/${id}`,
        method: "PUT",
        body: rest,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Affiliations", id },
        { type: "Affiliations", id: "BASE-LIST" },
      ],
    }),
    deleteAffiliation: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Affiliations", id },
        { type: "Affiliations", id: "BASE-LIST" },
      ],
    }),
  }),
});

export const {
  useGetAffiliationsQuery,
  useGetBaseAffiliationsQuery,
  useGetScholarshipAffiliationsQuery,
  useGetAffiliationByIdQuery,
  useGetAffiliationsWithScholarshipsQuery,
  useCreateAffiliationMutation,
  useUpdateAffiliationMutation,
  useDeleteAffiliationMutation,
} = affiliationsApi;

export default affiliationsApi;
export { affiliationsApi };
